import { useEffect, useRef, useState } from "react";
import api, { errorCode, errorMessage } from "../../lib/api";
import { RESULTS, RESULT_META, SEVERITIES, SEVERITY_META, looksLikeSecret } from "../../lib/equipment";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { AlertTriangle, Camera, Loader2, X } from "lucide-react";
import { toast } from "sonner";

export const newKey = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `k-${Date.now()}-${Math.random().toString(16).slice(2)}`);

const SECRET_HINT = "Ne notez jamais de mot de passe, code ou clé Wi-Fi : ils seraient refusés.";

function FieldError({ text }) {
  return text ? <p role="alert" className="text-sm text-red-700" data-testid="equipment-dialog-error">{text}</p> : null;
}

function SecretWarning({ values }) {
  return values.some(looksLikeSecret) ? (
    <p className="text-xs text-amber-800 flex items-center gap-1.5" data-testid="equipment-secret-warning"><AlertTriangle size={13} />{SECRET_HINT}</p>
  ) : null;
}

function useSubmit() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (request, onDone, onRefusal) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await request();
      await onDone(data);
    } catch (e) {
      if (!onRefusal?.(e)) setError(errorMessage(e, "Enregistrement impossible. Réessayez."));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, setError, submit };
}

// --- Asset (add / edit) -------------------------------------------------------------------------------------------------
// `url` + `method`: POST /missions/{id}/equipment or /prospects/{id}/equipment to add, PATCH /equipment/{id} to edit.
// `serialAllowed`: the serial field exists only for someone the server shows it to (decision 3).
export function AssetDialog({ open, onOpenChange, categories, asset, url, method = "post", extra = {}, serialAllowed, onDone }) {
  const empty = { category: "", manufacturer: "", model: "", serial_number: "", location: "", note: "" };
  const [form, setForm] = useState(empty);
  const [duplicate, setDuplicate] = useState(false);
  const [confirmDuplicate, setConfirmDuplicate] = useState(false);
  const req = useSubmit();
  useEffect(() => {
    if (!open) return;
    setForm(asset ? { ...empty, ...Object.fromEntries(Object.keys(empty).map((k) => [k, asset[k] || ""])) } : empty);
    setDuplicate(false); setConfirmDuplicate(false); req.setError("");
  }, [open, asset]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const active = (categories || []).filter((c) => c.active || c.code === asset?.category);

  const send = () => {
    const body = { ...extra };
    for (const [k, v] of Object.entries(form)) {
      if (k === "serial_number" && !serialAllowed) continue;
      const value = v.trim();
      if (asset) { if (value !== (asset[k] || "")) body[k] = value || null; } else if (value) body[k] = value;
    }
    if (asset) body.expected_version = asset.version;
    if (confirmDuplicate) body.confirm_warnings = ["duplicate_serial"];
    req.submit(() => api[method](url, body), (data) => { onDone(data); toast.success(asset ? "Équipement modifié" : "Équipement ajouté"); },
      (e) => { if (errorCode(e) === "warnings") { setDuplicate(true); return true; } return false; });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="equipment-asset-dialog">
        <DialogHeader>
          <DialogTitle>{asset ? "Modifier l'équipement" : "Ajouter un équipement"}</DialogTitle>
          <DialogDescription>{asset ? asset.label : "Il rejoint le parc matériel de la pharmacie."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Catégorie *</Label>
            <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
              <SelectTrigger data-testid="equipment-asset-category"><SelectValue placeholder="Choisir…" /></SelectTrigger>
              <SelectContent>{active.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label htmlFor="eq-manufacturer">Marque</Label>
              <Input id="eq-manufacturer" className="text-base md:text-sm" maxLength={120} value={form.manufacturer} onChange={set("manufacturer")} data-testid="equipment-asset-manufacturer" /></div>
            <div className="space-y-1.5"><Label htmlFor="eq-model">Modèle</Label>
              <Input id="eq-model" className="text-base md:text-sm" maxLength={120} value={form.model} onChange={set("model")} data-testid="equipment-asset-model" /></div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label htmlFor="eq-location">Emplacement</Label>
              <Input id="eq-location" className="text-base md:text-sm" maxLength={120} placeholder="Comptoir 1, réserve…" value={form.location} onChange={set("location")}
                data-testid="equipment-asset-location" /></div>
            {serialAllowed && (
              <div className="space-y-1.5"><Label htmlFor="eq-serial">Numéro de série</Label>
                <Input id="eq-serial" className="text-base md:text-sm" maxLength={120} value={form.serial_number} onChange={(e) => { set("serial_number")(e); setDuplicate(false); }}
                  data-testid="equipment-asset-serial" /></div>
            )}
          </div>
          <div className="space-y-1.5"><Label htmlFor="eq-note">Remarque</Label>
            <Textarea id="eq-note" rows={2} maxLength={1000} className="text-base md:text-sm" value={form.note} onChange={set("note")} data-testid="equipment-asset-note" /></div>
          <SecretWarning values={Object.values(form)} />
          {duplicate && (
            <label className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="equipment-asset-duplicate">
              <Checkbox checked={confirmDuplicate} onCheckedChange={(v) => setConfirmDuplicate(!!v)} className="mt-0.5" data-testid="equipment-asset-duplicate-confirm" />
              Un autre équipement de cette pharmacie porte déjà ce numéro de série. Cochez pour enregistrer quand même.
            </label>
          )}
          <FieldError text={req.error} />
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={req.busy}>Annuler</Button>
          <Button onClick={send} disabled={req.busy || !form.category || (duplicate && !confirmDuplicate)} data-testid="equipment-asset-save">
            {req.busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{asset ? "Enregistrer" : "Ajouter"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Retire --------------------------------------------------------------------------------------------------------------
export function RetireDialog({ open, onOpenChange, asset, onDone }) {
  const [reason, setReason] = useState("");
  const req = useSubmit();
  useEffect(() => { if (open) { setReason(""); req.setError(""); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!asset) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="equipment-retire-dialog">
        <DialogHeader>
          <DialogTitle>Retirer « {asset.label} » ?</DialogTitle>
          <DialogDescription>Il ne sera plus contrôlé, mais restera dans l'historique de la pharmacie avec le motif.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="eq-retire-reason">Motif *</Label>
          <Textarea id="eq-retire-reason" rows={2} maxLength={300} className="text-base md:text-sm" value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Remplacé, rendu, hors d'usage…" data-testid="equipment-retire-reason" />
          <FieldError text={req.error} />
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={req.busy}>Annuler</Button>
          <Button className="bg-red-600 hover:bg-red-700 text-white" disabled={req.busy || reason.trim().length < 3} data-testid="equipment-retire-confirm"
            onClick={() => req.submit(() => api.post(`/equipment/${asset.id}/retire`, { reason: reason.trim(), expected_version: asset.version }),
              (data) => { onDone(data); toast.success("Équipement retiré"); })}>
            {req.busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Retirer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Check ---------------------------------------------------------------------------------------------------------------
// Test an asset during the mission: four big result buttons, short measures, photos (camera on a phone), who did it.
// `corrects`: the check this one replaces (a reason is then required; the replaced one stays visible).
export function CheckDialog({ open, onOpenChange, mission, asset, performers, userId, corrects, onDone }) {
  const [result, setResult] = useState("");
  const [measures, setMeasures] = useState("");
  const [performer, setPerformer] = useState(`user:${userId}`);
  const [reason, setReason] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [key, setKey] = useState(newKey);
  const input = useRef(null);
  const req = useSubmit();
  useEffect(() => {
    if (!open) return;
    setResult(corrects?.result || ""); setMeasures(corrects?.measures || ""); setReason(""); setFiles([]); setKey(newKey()); req.setError("");
    setPerformer(corrects?.performed_by ? `${corrects.performed_by.kind}:${corrects.performed_by.id}` : `user:${userId}`);
  }, [open, corrects]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!asset) return null;

  const upload = async (picked) => {
    if (!picked.length) return;
    setUploading(true);
    try {
      for (const file of picked) {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post("/files", form, { params: { context: "equipment_check" }, headers: { "Idempotency-Key": newKey() } });
        setFiles((list) => [...list, { id: data.id, name: data.name || file.name }]);
      }
    } catch (e) {
      toast.error(errorMessage(e, "Photo refusée : image (JPEG, PNG, WebP) ou PDF de 10 Mo maximum."));
    } finally {
      setUploading(false);
      if (input.current) input.current.value = "";
    }
  };

  const send = () => {
    const [kind, id] = performer.split(":");
    const body = { asset_id: asset.id, result, file_ids: files.map((f) => f.id), performed_by: { kind, id } };
    if (measures.trim()) body.measures = measures.trim();
    if (corrects) { body.corrects = corrects.id; body.reason = reason.trim(); }
    req.submit(() => api.post(`/missions/${mission.id}/equipment-checks`, body, { headers: { "Idempotency-Key": key } }),
      (data) => onDone(data, { asset, result }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="equipment-check-dialog">
        <DialogHeader>
          <DialogTitle>{corrects ? "Corriger le contrôle" : "Tester"} : {asset.label}</DialogTitle>
          <DialogDescription>{corrects ? "L'ancien contrôle reste visible ; l'état de l'équipement suit la correction." : "Indiquez ce que vous avez constaté."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Résultat">
            {RESULTS.map((r) => (
              <button key={r} type="button" role="radio" aria-checked={result === r} onClick={() => setResult(r)} data-testid={`equipment-check-result-${r}`}
                className={`h-12 rounded-lg border text-sm font-medium flex items-center justify-center gap-2 ${result === r ? "border-jamora bg-jamora-50 text-jamora" : "border-slate-200 bg-white text-slate-700"}`}>
                <span className={`h-2.5 w-2.5 rounded-full ${RESULT_META[r].dot}`} aria-hidden />{RESULT_META[r].label}
              </button>
            ))}
          </div>
          <div className="space-y-1.5"><Label htmlFor="eq-measures">Constat ou mesures</Label>
            <Textarea id="eq-measures" rows={2} maxLength={1000} className="text-base md:text-sm" value={measures} onChange={(e) => setMeasures(e.target.value)}
              placeholder="Ex. : 10 codes lus, ticket imprimé, ne s'allume plus…" data-testid="equipment-check-measures" /></div>
          <SecretWarning values={[measures, reason]} />
          {performers.length > 1 && (
            <div className="space-y-1.5"><Label>Réalisé par</Label>
              <Select value={performer} onValueChange={setPerformer}>
                <SelectTrigger data-testid="equipment-check-performer"><SelectValue /></SelectTrigger>
                <SelectContent>{performers.map((p) => <SelectItem key={`${p.kind}:${p.id}`} value={`${p.kind}:${p.id}`}>{p.name}{p.kind === "collaborator" ? " (sans compte)" : ""}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-xs text-slate-500">Vous restez enregistré comme auteur de la saisie.</p>
            </div>
          )}
          <div className="space-y-2">
            {files.length > 0 && (
              <ul className="flex flex-wrap gap-2" data-testid="equipment-check-files">
                {files.map((f) => (
                  <li key={f.id} className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 pl-2 text-sm">
                    <span className="max-w-[12rem] truncate py-1">{f.name}</span>
                    <button type="button" aria-label={`Retirer ${f.name}`} onClick={() => setFiles((l) => l.filter((x) => x.id !== f.id))} className="p-1.5 text-slate-500 hover:text-red-700"><X size={13} /></button>
                  </li>
                ))}
              </ul>
            )}
            <input ref={input} type="file" className="hidden" multiple accept="image/jpeg,image/png,image/webp,application/pdf" capture="environment"
              onChange={(e) => upload(Array.from(e.target.files || []))} data-testid="equipment-check-file-input" />
            <Button type="button" variant="outline" className="h-11" disabled={uploading || files.length >= 6} onClick={() => input.current?.click()} data-testid="equipment-check-photo">
              {uploading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Camera size={16} className="mr-2" />}Prendre ou joindre une photo
            </Button>
            <p className="text-xs text-slate-500">Évitez les visages et les données des clients sur les photos.</p>
          </div>
          {corrects && (
            <div className="space-y-1.5"><Label htmlFor="eq-check-reason">Motif de la correction *</Label>
              <Textarea id="eq-check-reason" rows={2} maxLength={300} className="text-base md:text-sm" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="equipment-check-reason" /></div>
          )}
          <FieldError text={req.error} />
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={req.busy}>Annuler</Button>
          <Button onClick={send} disabled={req.busy || uploading || !result || (corrects && reason.trim().length < 3)} data-testid="equipment-check-save">
            {req.busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- New issue -----------------------------------------------------------------------------------------------------------
// From a mission (`missionId`) or directly on a pharmacy (`prospectId`, equipment:manage_all). `draft` pre-fills it from a faulty check.
export function IssueDialog({ open, onOpenChange, missionId, prospectId, assets, candidates, userId, draft, onDone }) {
  const empty = { asset_id: "", check_id: null, severity: "medium", title: "", symptoms: "", impact: "", attempted_action: "", responsible_user_id: userId, due_date: "" };
  const [form, setForm] = useState(empty);
  const [key, setKey] = useState(newKey);
  const req = useSubmit();
  useEffect(() => {
    if (!open) return;
    setForm({ ...empty, ...(draft || {}) }); setKey(newKey()); req.setError("");
  }, [open, draft]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const live = (assets || []).filter((a) => a.lifecycle !== "retired");

  const send = () => {
    const body = { severity: form.severity, title: form.title.trim(), responsible_user_id: form.responsible_user_id || userId };
    if (missionId) body.mission_id = missionId; else body.prospect_id = prospectId;
    for (const k of ["symptoms", "impact", "attempted_action"]) if (form[k].trim()) body[k] = form[k].trim();
    if (form.asset_id) body.asset_id = form.asset_id;
    if (form.check_id && form.asset_id === draft?.asset_id) body.check_id = form.check_id;
    if (form.due_date) body.due_date = form.due_date;
    req.submit(() => api.post("/equipment-issues", body, { headers: { "Idempotency-Key": key } }),
      (data) => { onDone(data); toast.success(`Anomalie ${data.number} ouverte`); });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="equipment-issue-dialog">
        <DialogHeader>
          <DialogTitle>Ouvrir une anomalie</DialogTitle>
          <DialogDescription>Elle sera suivie jusqu'à sa résolution par le responsable choisi.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label htmlFor="eq-issue-title">Titre *</Label>
            <Input id="eq-issue-title" className="text-base md:text-sm" maxLength={120} value={form.title} onChange={set("title")} data-testid="equipment-issue-title" /></div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Gravité *</Label>
              <Select value={form.severity} onValueChange={(v) => setForm((f) => ({ ...f, severity: v }))}>
                <SelectTrigger data-testid="equipment-issue-severity"><SelectValue /></SelectTrigger>
                <SelectContent>{SEVERITIES.map((s) => <SelectItem key={s} value={s}>{SEVERITY_META[s].label}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Équipement</Label>
              <Select value={form.asset_id || "none"} onValueChange={(v) => setForm((f) => ({ ...f, asset_id: v === "none" ? "" : v }))}>
                <SelectTrigger data-testid="equipment-issue-asset"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun en particulier</SelectItem>
                  {live.map((a) => <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>)}
                </SelectContent>
              </Select></div>
          </div>
          {form.severity === "critical" && <p className="text-xs text-red-700" data-testid="equipment-issue-critical-hint">Le responsable et les gestionnaires du matériel seront prévenus par WhatsApp.</p>}
          <div className="space-y-1.5"><Label htmlFor="eq-issue-symptoms">Symptômes</Label>
            <Textarea id="eq-issue-symptoms" rows={2} maxLength={1000} className="text-base md:text-sm" value={form.symptoms} onChange={set("symptoms")} data-testid="equipment-issue-symptoms" /></div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label htmlFor="eq-issue-impact">Impact pour la pharmacie</Label>
              <Textarea id="eq-issue-impact" rows={2} maxLength={1000} className="text-base md:text-sm" value={form.impact} onChange={set("impact")} data-testid="equipment-issue-impact" /></div>
            <div className="space-y-1.5"><Label htmlFor="eq-issue-action">Action déjà tentée</Label>
              <Textarea id="eq-issue-action" rows={2} maxLength={1000} className="text-base md:text-sm" value={form.attempted_action} onChange={set("attempted_action")}
                data-testid="equipment-issue-action" /></div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Responsable *</Label>
              <Select value={form.responsible_user_id} onValueChange={(v) => setForm((f) => ({ ...f, responsible_user_id: v }))}>
                <SelectTrigger data-testid="equipment-issue-responsible"><SelectValue /></SelectTrigger>
                <SelectContent>{(candidates || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.id === userId ? `${c.name} (moi)` : c.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label htmlFor="eq-issue-due">Échéance</Label>
              <Input id="eq-issue-due" type="date" className="text-base md:text-sm" value={form.due_date} onChange={set("due_date")} data-testid="equipment-issue-due" /></div>
          </div>
          <SecretWarning values={[form.title, form.symptoms, form.impact, form.attempted_action]} />
          <FieldError text={req.error} />
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={req.busy}>Annuler</Button>
          <Button onClick={send} disabled={req.busy || !form.title.trim()} data-testid="equipment-issue-save">
            {req.busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Ouvrir l'anomalie
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
